import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from "react";
import { clsx } from "clsx";

const SWIPE_TRIGGER_RATIO = 0.2;
const VERTICAL_CANCEL_PX = 12;
const SPRING_EASING = "cubic-bezier(0.25, 0.8, 0.25, 1)";
const MIN_SNAP_DURATION_MS = 260;
const MAX_SNAP_DURATION_MS = 650;
const DURATION_PER_TAB_MS = 220;

export interface Tab {
  id: string;
  label: string;
  count?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (id: string) => void;
  className?: string;
  renderContent?: (activeTab: string) => ReactNode;
  swipeable?: boolean;
}

type SwipeState = {
  startX: number;
  startY: number;
  startIndex: number;
  pointerId: number;
  pointerType: string;
};

export function Tabs({ tabs, activeTab, onChange, className, renderContent, swipeable = false }: TabsProps) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const initialIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));

  const [position, setPosition] = useState(initialIndex);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeState, setSwipeState] = useState<SwipeState | null>(null);
  const [snapDurationMs, setSnapDurationMs] = useState(400);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number; dragging: boolean }>({
    left: 0,
    width: 0,
    dragging: false,
  });
  const [containerWidth, setContainerWidth] = useState(0);

  const clampIndex = useCallback(
    (index: number) => Math.max(0, Math.min(tabs.length - 1, index)),
    [tabs.length]
  );

  const applyOverdragResistance = useCallback(
    (value: number) => {
      const min = 0;
      const max = Math.max(0, tabs.length - 1);
      if (value < min) {
        const overshoot = min - value;
        return min - overshoot / (1 + overshoot * 5);
      }
      if (value > max) {
        const overshoot = value - max;
        return max + overshoot / (1 + overshoot * 5);
      }
      return value;
    },
    [tabs.length],
  );

  const measureContainer = useCallback(() => {
    if (!contentRef.current) return;
    setContainerWidth(contentRef.current.offsetWidth || 1);
  }, []);

  useLayoutEffect(() => {
    measureContainer();
    if (!contentRef.current) return;

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measureContainer);
      observer.observe(contentRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measureContainer);
    return () => window.removeEventListener("resize", measureContainer);
  }, [measureContainer]);

  const getTabMetrics = useCallback(
    (tabId: string) => {
      const container = tabsRef.current;
      const element = tabRefs.current[tabId];
      if (!container || !element) return null;

      return {
        left: element.offsetLeft - container.scrollLeft,
        width: element.offsetWidth,
      };
    },
    [],
  );

  const updateIndicatorForPosition = useCallback(
    (pos: number, dragging: boolean) => {
      const lowerIndex = Math.floor(pos);
      const upperIndex = Math.min(tabs.length - 1, Math.ceil(pos));
      const lowerTab = tabs[lowerIndex];
      const upperTab = tabs[upperIndex];

      if (!lowerTab) return;

      const lowerMetrics = getTabMetrics(lowerTab.id);
      const upperMetrics = upperTab ? getTabMetrics(upperTab.id) ?? lowerMetrics : lowerMetrics;
      if (!lowerMetrics || !upperMetrics) return;

      const progress = upperIndex === lowerIndex ? 0 : pos - lowerIndex;
      const left = lowerMetrics.left + (upperMetrics.left - lowerMetrics.left) * progress;
      const width = lowerMetrics.width + (upperMetrics.width - lowerMetrics.width) * progress;

      setIndicatorStyle({ left, width, dragging });
    },
    [getTabMetrics, tabs],
  );

  // Keep the indicator in sync with the visual position.
  useEffect(() => {
    updateIndicatorForPosition(position, Boolean(swipeState));
  }, [position, swipeState, updateIndicatorForPosition]);

  // Ensure the active tab button stays in view.
  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeElement = container.querySelector(`[data-tab-id="${activeTab}"]`) as HTMLElement | null;
    if (!activeElement) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const leftGap = activeRect.left - containerRect.left;
    const rightGap = activeRect.right - containerRect.right;

    if (leftGap < 20) {
      container.scrollTo({ left: container.scrollLeft + leftGap - 20, behavior: "smooth" });
    } else if (rightGap > -20) {
      container.scrollTo({ left: container.scrollLeft + rightGap + 20, behavior: "smooth" });
    }
  }, [activeTab]);

  // Animate to an externally selected tab (e.g., via click or URL change).
  useEffect(() => {
    if (swipeState) return;
    const targetIndex = clampIndex(tabs.findIndex((tab) => tab.id === activeTab));
    if (Number.isNaN(targetIndex)) return;
    if (Math.abs(position - targetIndex) < 0.001) return;

    const distance = Math.abs(position - targetIndex);
    const duration = Math.min(
      MAX_SNAP_DURATION_MS,
      Math.max(MIN_SNAP_DURATION_MS, MIN_SNAP_DURATION_MS + distance * DURATION_PER_TAB_MS)
    );
    setSnapDurationMs(duration);
    setIsAnimating(true);
    setPosition(targetIndex);
  }, [activeTab, clampIndex, position, swipeState, tabs]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!swipeable || !event.isPrimary) return;

    const pointerType = event.pointerType;
    const isTouchLike = pointerType === "touch" || pointerType === "pen";
    const isMouseLike = pointerType === "mouse" && event.button === 0;
    if (!isTouchLike && !isMouseLike) return;

    if (event.target instanceof HTMLElement && event.target.closest("button, a, input, select, textarea, [data-tab-interactive]")) {
      return;
    }

    setIsAnimating(false);
    setSwipeState({
      startX: event.clientX,
      startY: event.clientY,
      startIndex: clampIndex(Math.round(position)),
      pointerId: event.pointerId,
      pointerType,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!swipeState || swipeState.pointerId !== event.pointerId) return;
    if (containerWidth <= 0) return;

    const deltaX = event.clientX - swipeState.startX;
    const deltaY = event.clientY - swipeState.startY;

    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > VERTICAL_CANCEL_PX) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      setSwipeState(null);
      setIsAnimating(false);
      setPosition(clampIndex(Math.round(position)));
      return;
    }

    const relativeDelta = deltaX / Math.max(containerWidth, 1);
    const nextPosition = applyOverdragResistance(swipeState.startIndex - relativeDelta);
    setIsAnimating(false);
    setPosition(nextPosition);
  };

  const handlePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!swipeState || swipeState.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (containerWidth <= 0) {
      setSwipeState(null);
      return;
    }

    const deltaX = event.clientX - swipeState.startX;
    const relativeDelta = deltaX / Math.max(containerWidth, 1);
    const movedEnough = Math.abs(relativeDelta) > SWIPE_TRIGGER_RATIO;
    const direction = relativeDelta < 0 ? 1 : -1;

    const targetIndex = movedEnough
      ? clampIndex(swipeState.startIndex + direction)
      : clampIndex(swipeState.startIndex);

    const distance = Math.abs(position - targetIndex);
    const duration = Math.min(
      MAX_SNAP_DURATION_MS,
      Math.max(MIN_SNAP_DURATION_MS, MIN_SNAP_DURATION_MS + distance * DURATION_PER_TAB_MS)
    );
    setSnapDurationMs(duration);
    setSwipeState(null);
    setIsAnimating(true);
    setPosition(targetIndex);

    const targetTab = tabs[targetIndex];
    if (targetTab && targetTab.id !== activeTab) {
      onChange(targetTab.id);
    }
  };

  const handleTabClick = (tabId: string, index: number) => {
    const distance = Math.abs(position - index);
    const duration = Math.min(
      MAX_SNAP_DURATION_MS,
      Math.max(MIN_SNAP_DURATION_MS, MIN_SNAP_DURATION_MS + distance * DURATION_PER_TAB_MS)
    );
    setSnapDurationMs(duration);
    setIsAnimating(true);
    setPosition(index);
    if (tabId !== activeTab) {
      onChange(tabId);
    }
  };

  const handleTransitionEnd = () => {
    setIsAnimating(false);
  };

  const translateX = -(position * Math.max(containerWidth, 1));
  const contentTransition =
    !swipeState && isAnimating
      ? `transform ${snapDurationMs}ms ${SPRING_EASING}`
      : undefined;

  return (
    <div className={clsx("flex flex-col", className)}>
      {/* Tab List */}
      <div className="relative border-b border-slate-200 dark:border-slate-800">
        <div
          ref={tabsRef}
          role="tablist"
          className="flex w-full overflow-x-auto overflow-y-hidden overscroll-contain scroll-smooth no-scrollbar"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              ref={(node) => {
                if (node) {
                  tabRefs.current[tab.id] = node;
                } else {
                  delete tabRefs.current[tab.id];
                }
              }}
              data-tab-id={tab.id}
              id={`tab-${tab.id}`}
              onClick={() => handleTabClick(tab.id, index)}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              className={clsx(
                "relative flex flex-none items-center gap-2 px-3 py-3 text-sm font-semibold transition-colors duration-200 ease-out select-none outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900",
                activeTab === tab.id
                  ? "text-primary-700 dark:text-primary-200"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={clsx(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none",
                    activeTab === tab.id
                      ? "bg-primary-600 text-white shadow-sm dark:bg-primary-500 dark:text-slate-900"
                      : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}

          {/* Animated Indicator */}
          <div
            className={clsx(
              "absolute bottom-0 h-[3px] bg-primary-600 dark:bg-primary-400",
              indicatorStyle.dragging ? "transition-none" : "transition-all"
            )}
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              transitionDuration: indicatorStyle.dragging ? "0ms" : `${snapDurationMs}ms`,
              transitionTimingFunction: SPRING_EASING,
            }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0">
        <div
          className="relative h-full overflow-hidden"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          ref={contentRef}
        >
          <div
            className={clsx(
              "grid h-full w-full grid-flow-col auto-cols-[100%] touch-pan-y",
              swipeState ? "transition-none" : undefined
            )}
            style={{
              transform: `translateX(${translateX}px)`,
              transition: contentTransition,
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {tabs.map((tab) => (
              <div
                key={tab.id}
                role="tabpanel"
                id={`panel-${tab.id}`}
                aria-labelledby={`tab-${tab.id}`}
                aria-hidden={activeTab !== tab.id}
                className="h-full min-h-0 focus:outline-none"
              >
                {renderContent ? renderContent(tab.id) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
